/**
 * Canonical section ids for POST /api/upload/{session_id} (document_type).
 * Must match backend `document_sections.DOCUMENT_SECTION_IDS`.
 */
export const CASE_DOCUMENT_SECTION = {
  employment_section: "employment_section",
  income_proof: "income_proof",
  pay_slips: "pay_slips",
  bank_statements: "bank_statements",
  proof_of_residence_section: "proof_of_residence_section",
  nie_cert: "nie_cert",
  /** Criminal record certificate uploads (distinct from NIE certificate `nie_cert`). */
  criminal_record_certificate: "criminal_record_certificate",
  valuation: "valuation",
  employment_contract: "employment_contract",
  identity_section: "identity_section",
  tax_return: "tax_return",
  asylum_section: "asylum_section",
  prior_denial_documents: "prior_denial_documents",
  family_section: "family_section",
} as const;

/** `document_type` for prior-denial flow; normalizes to `prior_denial_documents` in storage. */
export const PRIOR_DENIAL_CRIMINAL_DOCUMENT_TYPE = "prior_denial_criminal_record" as const;

export type CaseDocumentSectionId = (typeof CASE_DOCUMENT_SECTION)[keyof typeof CASE_DOCUMENT_SECTION];
export type ResidenceDocumentType =
  | "padron_municipal_registration_id"
  | "rental_contract_id"
  | "medical_record_id"
  | "childrens_school_enrollment_id"
  | "bank_statement_id"
  | "utility_bill_id"
  | "payslip_work_contract_id"
  | "money_transfer_receipt_id"
  | "course_training_certificate_id"
  | "transport_ticket_id"
  | "police_report_id"
  | "official_letter_notification_id"
  | "other";
export type EmploymentDocumentType =
  | "work_contract_id"
  | "nomina_id"
  | "vida_laboral_id"
  | "job_offer_letter_id"
  | "other_documents_id"
  | "self_employment_id";
export type FamilyDocumentType =
  | "marriage_certificate_id"
  | "libro_de_familia_id"
  | "childrens_birth_certificates_id"
  | "proof_of_cohabitation_id"
  | "school_enrollment_id"
  | "other_documents_id";

/** StayUploadList tier keys (section7.docType*) → admin Documents tab slot. */
export function mapStayTierKeyToSection(tierKey: string): CaseDocumentSectionId {
  if (!tierKey.trim()) return CASE_DOCUMENT_SECTION.proof_of_residence_section;
  return CASE_DOCUMENT_SECTION.proof_of_residence_section;
}

/** StayUploadList tier keys (section7.docType*) -> uploaded document_type value. */
export function mapStayTierKeyToDocumentType(tierKey: string): ResidenceDocumentType {
  const map: Record<string, ResidenceDocumentType> = {
    Payslip: "payslip_work_contract_id",
    Bank: "bank_statement_id",
    Utility: "utility_bill_id",
    Padron: "padron_municipal_registration_id",
    Rental: "rental_contract_id",
    Medical: "medical_record_id",
    School: "childrens_school_enrollment_id",
    Transfer: "money_transfer_receipt_id",
    Course: "course_training_certificate_id",
    Transport: "transport_ticket_id",
    Police: "police_report_id",
    Official: "official_letter_notification_id",
    Other: "other",
  };
  return map[tierKey] ?? "other";
}

/** StayUploadList tier keys (section7.docType*) -> family uploaded document_type value. */
export function mapStayTierKeyToFamilyDocumentType(tierKey: string): FamilyDocumentType {
  const map: Record<string, FamilyDocumentType> = {
    Padron: "other_documents_id",
    Rental: "other_documents_id",
    Medical: "other_documents_id",
    School: "school_enrollment_id",
    Bank: "other_documents_id",
    Utility: "other_documents_id",
    Payslip: "other_documents_id",
    Transfer: "other_documents_id",
    Course: "other_documents_id",
    Transport: "other_documents_id",
    Police: "other_documents_id",
    Official: "other_documents_id",
    Other: "other_documents_id",
  };
  return map[tierKey] ?? "other_documents_id";
}

/** Asylum doc subtype (receipt | resolution | other) → section. */
export function mapAsylumDocTypeToSection(_asylumDocType: string): CaseDocumentSectionId {
  return CASE_DOCUMENT_SECTION.asylum_section;
}

/**
 * DocUpload residence modal `<option value="...">` → API section (not all tier keys exist in StayUploadList).
 */
export function mapModalResidenceDocTypeToSection(modalType: string): CaseDocumentSectionId {
  return CASE_DOCUMENT_SECTION.proof_of_residence_section;
}

/** Residence modal type -> uploaded document_type value. */
export function mapModalResidenceDocTypeToDocumentType(modalType: string): ResidenceDocumentType {
  const m: Record<string, ResidenceDocumentType> = {
    empadronamiento: "padron_municipal_registration_id",
    rental: "rental_contract_id",
    medical: "medical_record_id",
    school: "childrens_school_enrollment_id",
    bank: "bank_statement_id",
    utility: "utility_bill_id",
    employment: "payslip_work_contract_id",
    transfer: "money_transfer_receipt_id",
    course: "course_training_certificate_id",
    transport: "transport_ticket_id",
    police: "police_report_id",
    official: "official_letter_notification_id",
    other: "other",
  };
  return m[modalType] ?? "other";
}

/**
 * Modal value → StayUploadList tier key (for shared `stayDocuments` + checklist).
 */
export function mapModalResidenceDocTypeToStayTierKey(modalType: string): string {
  const m: Record<string, string> = {
    empadronamiento: "Padron",
    rental: "Rental",
    medical: "Medical",
    bank: "Bank",
    utility: "Utility",
    employment: "Payslip",
    other: "Other",
  };
  return m[modalType] ?? "Other";
}
